import { Field, Form, Location, LocationFilter, LogicFilter } from '../../types/recap'
import { computed, ref, Ref } from 'vue'
import { getCurrentStore } from '../../utils'
import { useStoreRecaps } from '../../store/useStoreRecaps'
import { useRecapStore } from '../../store/useRecapStore'
import { storeToRefs } from 'pinia'

export function useDisplayLogic(
  recapId: string,
  taskOrStoreId: Ref<string>,
  recapType: Ref<'task' | 'store'>
) {
  const shouldBeHidden = ref(false)

  const storeRecapKey = computed(() => `${taskOrStoreId.value}-${recapId}`)
  let recapsStore: any = null
  if (recapType.value === 'store') {
    recapsStore = storeToRefs(useStoreRecaps())
  } else {
    recapsStore = storeToRefs(useRecapStore())
  }

  function handleAllDisplayLogic(formOrField: Form | Field) {
    handleLocationVariables(formOrField.location_variable_ids)
    if (!shouldBeHidden.value && formOrField.role_variables) {
      // Run Role Variables
      handleRoleVariables(formOrField.role_variables.map((rv) => rv.role_variables_id))
    }
    if (!shouldBeHidden.value) {
      // Run Location Filters
      const locationFilters = formOrField.logic_groups
        .map((group) => group.filters)
        .filter((filter) => filter)
        .flat()
      handleLocationFilters(locationFilters)
    }
    if (!shouldBeHidden.value && formOrField.locations) {
      // Run Locations
      handleLocations(formOrField.locations)
    }
    if (!shouldBeHidden.value) {
      // Run Logic Filters
      const logicFilters = formOrField.logic_groups
        .map((group) => group.logic_filters)
        .filter((filter) => filter)
        .flat()
      handleLogicFilters(logicFilters)
    }
  }

  function handleLocationVariables(locationVariableIds: string[]) {
    if (locationVariableIds.length) {
      const currentStore = getCurrentStore()
      if (currentStore) {
        // TODO: Add location variables to stores - this will currently fail
        shouldBeHidden.value = !locationVariableIds.some((id) =>
          (currentStore?.location_variable_ids as unknown as string[]).includes(id)
        )
      }
    }
  }

  function handleRoleVariables(roleVariableIds: string[]) {
    if (roleVariableIds.length) {
      shouldBeHidden.value = !roleVariableIds.some(
        (id) => id === recapsStore.recaps.value[storeRecapKey.value].recapDefinition?.user_role_id
      )
    }
  }

  function handleLocationFilters(locationFilters: (LocationFilter | undefined)[]) {
    if (locationFilters && locationFilters.length) {
      const currentStore = getCurrentStore()
      shouldBeHidden.value = !locationFilters.every((filter) => {
        if (filter && filter.value) {
          switch (filter.type) {
            case 'chain':
              if (filter.subtype === 'child chain') {
                return (
                  +filter.value ===
                  recapsStore.recaps.value[storeRecapKey.value].recapDefinition?.current_location
                    ?.chains_id
                )
              } else if (filter.parent_value) {
                return (
                  +filter.parent_value ===
                  recapsStore.recaps.value[storeRecapKey.value].recapDefinition?.current_location
                    ?.parent_chains_id
                )
              } else {
                return false
              }
            case 'city':
              return filter.value === currentStore?.city
            case 'market':
              return filter.value === currentStore?.markets_id
            case 'state':
              return filter.value === currentStore?.state
            case 'zip':
              return filter.value === currentStore?.zip
          }
        } else {
          return false
        }
      })
    }
  }

  function handleLocations(locations: Location[]) {
    if (locations.length) {
      const currentStore = getCurrentStore()
      shouldBeHidden.value = !locations.some((loc) => loc.location_id === currentStore?.store_id)
    }
  }

  function handleLogicFilters(logicFilters: (LogicFilter | undefined)[]) {
    if (logicFilters.length) {
      shouldBeHidden.value = !logicFilters.every((logicFilter) => {
        if (logicFilter) {
          const data =
            recapsStore.recaps.value[storeRecapKey.value].recapDefinition?.logic_filters_data
          if (data) {
            return data[logicFilter.custom_filters_id] === +logicFilter.value
          }
          return false
        }
        return true
      })
    }
  }

  return {
    shouldBeHidden,
    handleAllDisplayLogic,
  }
}
